/* eslint-disable @typescript-eslint/no-unused-vars */
/*
 * Copyright (c) 2020. American Express Company
 */

// theirs
import _ from 'lodash';
import React, { useState } from 'react';
import { LocalDate } from '@js-joda/core';

// models and services
import { BwAlertType } from 'src/types/view-models/bw-alert.view.model';
import { IncomeSourceModel, TransactionIncomeSourceValueModel } from 'src/types/income-source.model';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';
import { IncomesDrawerViewModel } from 'src/types/view-models/incomes-drawer.view.model';
import IncomeSourceService from 'src/services/income-source.service';

// components
import { ButtonPrimary, IconActivity, IconSetting } from '@americanexpress/dls-react';
import BwAlert from '../../components/alert/BwAlert';
import BwSlider from '../../components/bw-slider/BwSlider';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import Drawer from '../../components/drawer/Drawer';
import DrawerIntro from '../../components/drawer-intro/DrawerIntro';
import EmptyStateBox from '../../components/empty-state-box/EmptyStateBox';
import MoreInfoTooltip from '../../components/more-info-tooltip/MoreInfoTooltip';
import Output from '../../components/atomic/molecules/output/Output';
import SectionHeader from '../../components/section-header/SectionHeader';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';

// icons and css
import { DEFAULT_INCOME_GROWTH, HELP_DOMAIN, PRODUCT_NAME } from 'src/utils/constants';
import styles from './IncomesDrawer.module.scss';
import Selectors from 'src/utils/selectors';

export default function IncomesDrawer({
    user,
    drawerProps,
    handleAddIncome,
    handleBack,
    handleEditIncome,
    handleOpenIncome,
    handleOpenIncomeSummary,
    handleOpenReviewIncomeTransactions,
    handleReloadUser,
    handleUpdateUserAttribute
}: IncomesDrawerViewModel): JSX.Element {
    const [incomeGrowth, setIncomeGrowth] = useState(
        user.attributes.incomeGrowth !== undefined ? user.attributes.incomeGrowth : DEFAULT_INCOME_GROWTH
    );
    const incomeGrowthUrl: string = HELP_DOMAIN ? HELP_DOMAIN + '/articles/income' : null;

    /** Get income sources, both manually created and transaction incomes */
    const manualIncomeSources: IncomeSourceModel[] = IncomeSourceService.getManualIncomes(drawerProps.incomeSources);
    const transactionIncomeSources: IncomeSourceModel[] = IncomeSourceService.getTransactionIncomes(
        drawerProps.transactions,
        drawerProps.incomeSources
    );
    const combinedIncomeSources: IncomeSourceModel[] = manualIncomeSources.concat(transactionIncomeSources);
    const transactionIncomesSourcesByValue: TransactionIncomeSourceValueModel =
        IncomeSourceService.getTransactionIncomesByValue(drawerProps.transactions);
    const allIncomeSourcesTotal: number = IncomeSourceService.getTotalIncome(
        drawerProps.transactions,
        drawerProps.incomeSources
    );

    const drawerTitle: string =
        drawerProps.persons?.length > 1
            ? LiteralsService.getLiteral('IncomesDrawer.titleFamily')
            : LiteralsService.getLiteral('IncomesDrawer.titleSingle');

    class FormatIncomeGroupProps {
        combinedIncomeSources: IncomeSourceModel[];
        transactionIncomesSourcesByValue: TransactionIncomeSourceValueModel;
        startDate: LocalDate;
        endDate: LocalDate;
    }

    const FormatIncomeGroup = ({
        combinedIncomeSources,
        transactionIncomesSourcesByValue,
        startDate,
        endDate
    }: FormatIncomeGroupProps): JSX.Element => {
        const onIncomeClick = (income: IncomeSourceModel): void => {
            if (!income.userCreated) {
                handleOpenIncome(income, startDate, endDate, () => {
                    handleOpenIncomeSummary(startDate, endDate);
                });
            } else {
                handleEditIncome(income, startDate, endDate);
            }
        };

        return (
            <>
                {combinedIncomeSources
                    .sort((a: IncomeSourceModel, b: IncomeSourceModel) => b.annualizedIncome - a.annualizedIncome)
                    .map((income, index) => {
                        if (income.annualizedIncome === 0) {
                            return null;
                        }
                        const icon: JSX.Element = !income.userCreated ? <IconActivity /> : <IconSetting />;
                        const manualText: JSX.Element = !income.userCreated ? null : (
                            <span className="small">Added Manually</span>
                        );
                        const total: number = !income.userCreated
                            ? _.get(transactionIncomesSourcesByValue, income.incomeUid)
                            : income.annualizedIncome;
                        if (!total) return null;
                        return (
                            <div className={styles.incomeRow} key={index}>
                                <Anchor
                                    onClick={() => onIncomeClick(income)}
                                    typographyProps={{ variant: TypographyVariants.BODY_BOLD }}
                                >
                                    {income.name}
                                </Anchor>
                                <div className={styles.rowRight}>
                                    <div className={styles.incomeTotal}>
                                        <Typography variant={TypographyVariants.HEADING_S}>
                                            {FormatService.format(total)}
                                        </Typography>
                                        {manualText}
                                    </div>
                                    <ClickableIcon icon={icon} onClick={() => onIncomeClick(income)} />
                                </div>
                            </div>
                        );
                    })}
            </>
        );
    };

    const intro: JSX.Element = (
        <DrawerIntro
            header={LiteralsService.getLiteral('IncomesDrawer.header')}
            content={LiteralsService.getLiteral('IncomesDrawer.intro', [PRODUCT_NAME])}
            learnMoreUrl="#"
            // TODO: add correct url above
            learnMoreContent={LiteralsService.getLiteral('IncomesDrawer.learnMore')}
        />
    );

    const reviewButton: JSX.Element | null = user.incomeTransactionsToReviewCount > 0 && (
        <BwAlert
            text=""
            action={`${user.expenseTransactionsToReviewCount} transactions to review`}
            type={BwAlertType.NEUTRAL}
            fullWidth={true}
            onClick={() => handleOpenReviewIncomeTransactions(false)}
            key={'stepZeroIncome'}
            className="before24"
        />
    );

    return (
        <Drawer
            drawerProps={drawerProps}
            handleBack={handleBack}
            title={drawerTitle}
            withBackButton
            className={styles.incomesDrawer}
        >
            {intro}

            <Output
                title={LiteralsService.getLiteral('IncomesDrawer.outputTitle')}
                main={FormatService.format(allIncomeSourcesTotal)}
            />

            <div>
                <ButtonPrimary
                    onClick={() => {
                        handleAddIncome();
                    }}
                    data-cy={Selectors.PlanAhead.ADD_INCOME_BUTTON}
                >
                    {LiteralsService.getLiteral('IncomesDrawer.addButton')}
                </ButtonPrimary>
            </div>

            {reviewButton}

            <div className="drawerSection">
                <SectionHeader>{LiteralsService.getLiteral('IncomesDrawer.sourcesHeader')}</SectionHeader>
                {manualIncomeSources.length > 0 || transactionIncomeSources.length > 0 ? (
                    <FormatIncomeGroup
                        combinedIncomeSources={combinedIncomeSources}
                        transactionIncomesSourcesByValue={transactionIncomesSourcesByValue}
                        startDate={drawerProps.startDate}
                        endDate={drawerProps.endDate}
                    />
                ) : (
                    <EmptyStateBox>{LiteralsService.getLiteral('IncomesDrawer.noIncomes')}</EmptyStateBox>
                )}
            </div>

            <div>
                <div className={styles.growthRateHeader}>
                    <Typography variant={TypographyVariants.BODY_BOLD}>
                        {LiteralsService.getLiteral('IncomesDrawer.growthRateHeader')}
                    </Typography>
                    <MoreInfoTooltip
                        title={LiteralsService.getLiteral('IncomesDrawer.growthTooltipTitle')}
                        buttonAriaLabel={LiteralsService.getLiteral('IncomesDrawer.growthTooltipTitle')}
                        body={LiteralsService.getJsxLiteral('IncomesDrawer.growthTooltip')}
                        link={incomeGrowthUrl}
                        linkName={LiteralsService.getJsxLiteral('IncomesDrawer.growthLearnMore')}
                    />
                </div>
                <Typography component="div" variant={TypographyVariants.BODY} className={styles.growthRateBody}>
                    {LiteralsService.getLiteral('IncomesDrawer.growthRateBody')}
                </Typography>
                <BwSlider
                    value={100 * incomeGrowth}
                    label={LiteralsService.getLiteral('IncomesDrawer.growthRateHeader')}
                    hideLabel
                    formattedValue={FormatService.formatPercent(100 * incomeGrowth)}
                    step={0.1}
                    min={0}
                    max={5}
                    onChange={async (value: number) => {
                        setIncomeGrowth(value / 100);
                    }}
                    onDone={async () => {
                        handleUpdateUserAttribute('incomeGrowth', incomeGrowth);
                        handleReloadUser();
                    }}
                    isPercentage
                />
            </div>
        </Drawer>
    );
}
